import React, { useState, useEffect } from 'react'
import * as rrweb from 'rrweb'
import StackTrace from 'stacktrace-js'
import { insertErrorMonitoringLog } from './services/api/errorMonitoring'

let eventBuffer = []
let stopRecording = null

// Start recording user interactions and page events
export function startBufferedRecording() {
    stopRecording = rrweb.record({
        emit(event) {
            const now = Date.now()
            eventBuffer.push({ event, timestamp: now })
            // Retain only the last 10 seconds of events
            eventBuffer = eventBuffer.filter(e => now - e.timestamp <= 10000)
        },
        recordCanvas: true,
        sampling: {
            mousemove: true,
        },
    })
}

// Export stopRecording to allow its use in other files
export { stopRecording }

// Upload error data and session replay information
async function uploadErrorData(error, info, userId) {
    if (stopRecording) stopRecording()

    const replayData = eventBuffer.map(e => e.event)
    const events = replayData.length >= 2 ? replayData : []

    // Use stacktrace-js to map the stack to the original source
    let errorStack = ''
    try {
        const stackFrames = await StackTrace.fromError(error)
        errorStack = stackFrames.map(frame => {
            const functionName = frame.functionName || 'anonymous'
            const fileName = frame.fileName
            const lineNumber = frame.lineNumber
            const columnNumber = frame.columnNumber
            return `at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`
        }).join('\n')
    } catch (stackErr) {
        console.error('Error parsing stack trace:', stackErr)
        errorStack = error.stack || 'No error stack available'
    }

    // Get the component name
    let errorComponent = 'Unknown Component'
    const componentStack = info?.componentStack || ''

    const payload = {
        errorMessage: error.message || error.toString(),
        componentStack: componentStack,
        errorStack: errorStack,
        errorComponent: errorComponent,
        events: events,
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
        env: process.env.NODE_ENV || 'development',
        userId,
        handled: true,
    }

    console.log('Error Payload:', payload)

    try {
        const response = await insertErrorMonitoringLog(payload)
        if (response.failure) {
            console.error('Error logging failed:', response.data?.error)
        }
    } catch (e) {
        console.error('Error uploading monitoring data:', e)
    }
}

// Error boundary to catch JavaScript errors and unhandled promise rejections
export function ErrorBoundary({
    children,
    userId,
    fallback = <h2>Something went wrong. Please refresh the page.</h2>,
    onError,
}) {
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        const errorHandler = (event) => {
            setHasError(true)
            const error = event.error || new Error(event.message)
            const info = { 
                componentStack: error.componentStack || '',
                error: error
            }
            uploadErrorData(error, info, userId)
            onError?.(error, info)
        }

        const rejectionHandler = (event) => {
            setHasError(true)
            const error = event.reason instanceof Error ? event.reason : new Error(event.reason)
            const info = { 
                componentStack: error.componentStack || '',
                error: error
            }
            uploadErrorData(error, info, userId)
            onError?.(error, info)
        }

        // Attach global error event listeners
        window.addEventListener('error', errorHandler)
        window.addEventListener('unhandledrejection', rejectionHandler)

        return () => {
            // Cleanup event listeners on component unmount
            window.removeEventListener('error', errorHandler)
            window.removeEventListener('unhandledrejection', rejectionHandler)
        }
    }, [userId, onError])

    if (hasError) return fallback
    return children
}
